import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  decryptData,
  encryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";
import { cloudflareApiUrl, cloudflareFetch } from "./cloudflare";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { CollabSocket } from "../collab/CloudflareSocket";

type StoredScene = {
  sceneVersion: number;
  iv: string;
  ciphertext: string;
};

const ROOM_SCENE_RETRY_COUNT = 2;

const bytesToBase64Url = (value: ArrayBuffer | Uint8Array) => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";

  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const base64UrlToBytes = (value: string) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const filePathFromPrefix = (prefix: string, id: FileId) => {
  const parts = prefix.replace(/^\/+/, "").split("/");
  let namespace = parts[1];
  let ownerId = parts[2];

  if (parts[0] === "migrations") {
    namespace = "migrations";
    ownerId = parts[parts.length - 1];
  }

  if (!namespace || !ownerId) {
    throw new Error(`Unsupported file storage prefix: ${prefix}`);
  }

  return cloudflareApiUrl(
    `/api/files/${encodeURIComponent(namespace)}/${encodeURIComponent(
      ownerId,
    )}/${encodeURIComponent(id)}`,
  );
};

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<StoredScene> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);

  return {
    sceneVersion: getSceneVersion(elements),
    ciphertext: bytesToBase64Url(encryptedBuffer),
    iv: bytesToBase64Url(iv),
  };
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const decrypted = await decryptData(
    base64UrlToBytes(data.iv),
    base64UrlToBytes(data.ciphertext),
    roomKey,
  );
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

class CloudflareSceneVersionCache {
  private static cache = new WeakMap<CollabSocket, number>();
  static get = (socket: CollabSocket) => {
    return CloudflareSceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: CollabSocket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    CloudflareSceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return CloudflareSceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await cloudflareFetch(filePathFromPrefix(prefix, id), {
          method: "PUT",
          body: buffer.buffer.slice(
            buffer.byteOffset,
            buffer.byteOffset + buffer.byteLength,
          ) as ArrayBuffer,
          headers: {
            "content-type": MIME_TYPES.binary,
          },
        });

        if (!response.ok) {
          throw new Error(`File upload failed with HTTP ${response.status}`);
        }

        savedFiles.push(id);
      } catch (error: any) {
        console.error(error);
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

const loadStoredScene = async (roomId: string) => {
  const response = await cloudflareFetch(
    cloudflareApiUrl(`/api/rooms/${encodeURIComponent(roomId)}/scene`),
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Room load failed with HTTP ${response.status}`);
  }

  return (await response.json()) as StoredScene;
};

const saveStoredScene = async ({
  roomId,
  storedScene,
  prevSceneVersion,
}: {
  roomId: string;
  storedScene: StoredScene;
  prevSceneVersion: number | null;
}) => {
  const response = await cloudflareFetch(
    cloudflareApiUrl(`/api/rooms/${encodeURIComponent(roomId)}/scene`),
    {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...storedScene,
        prevSceneVersion,
      }),
    },
  );

  if (response.status === 409) {
    return {
      conflict: true as const,
      storedScene: ((await response.json()) as { scene: StoredScene }).scene,
    };
  }

  if (!response.ok) {
    throw new Error(`Room save failed with HTTP ${response.status}`);
  }

  return {
    conflict: false as const,
    storedScene: (await response.json()) as StoredScene,
  };
};

const createCloudflareSceneDocument = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
) => encryptElements(roomKey, elements);

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  let remoteStoredScene = await loadStoredScene(roomId);
  let elementsToStore = elements;

  for (let attempt = 0; attempt <= ROOM_SCENE_RETRY_COUNT; attempt++) {
    if (remoteStoredScene) {
      const prevStoredElements = getSyncableElements(
        restoreElements(await decryptElements(remoteStoredScene, roomKey), null),
      );

      elementsToStore = getSyncableElements(
        reconcileElements(
          elements,
          prevStoredElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
    }

    const storedScene = await createCloudflareSceneDocument(
      elementsToStore,
      roomKey,
    );
    const result = await saveStoredScene({
      roomId,
      storedScene,
      prevSceneVersion: remoteStoredScene?.sceneVersion ?? null,
    });

    if (!result.conflict) {
      const storedElements = getSyncableElements(
        restoreElements(await decryptElements(result.storedScene, roomKey), null),
      );

      CloudflareSceneVersionCache.set(socket, storedElements);

      return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
    }

    remoteStoredScene = result.storedScene;
  }

  throw new Error("Failed to save collaboration room after conflict retries");
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: CollabSocket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const storedScene = await loadStoredScene(roomId);
  if (!storedScene) {
    return null;
  }

  const elements = getSyncableElements(
    restoreElements(await decryptElements(storedScene, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );

  if (socket) {
    CloudflareSceneVersionCache.set(socket, elements);
  }

  return elements;
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await cloudflareFetch(filePathFromPrefix(prefix, id));
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
